/**
 * LanguagePickerScreen
 * Shown on first visit (no language in localStorage yet).
 * Full-screen overlay, disappears after language is chosen.
 */
import { createSignal, onMount, Show } from 'solid-js';
import { useLanguage } from '../context/LanguageContext';
import { storage } from '../utils/storage';

type LangStatus = 'full' | 'beta' | 'wip';

// fr/es/pl/nl/it/pt sind nicht übersetzt (~30 von 4600 Texten) → vorerst nicht anbieten
const LANGUAGES: { code: string; flag: string; native: string; status: LangStatus }[] = [
  { code: 'de', flag: '🇩🇪', native: 'Deutsch',    status: 'full' },
  { code: 'en', flag: '🇬🇧', native: 'English',    status: 'beta' },
];

const STATUS_BADGE: Record<LangStatus, { label: string; cls: string }> = {
  full: { label: '✓ 100%',        cls: 'lps-badge lps-badge--full' },
  beta: { label: '✓ ~100%',       cls: 'lps-badge lps-badge--beta' },
  wip:  { label: '🚧 In Arbeit',  cls: 'lps-badge lps-badge--wip'  },
};

export default function LanguagePickerScreen(props: { children: import('solid-js').JSX.Element }) {
  const { setLanguage } = useLanguage();
  const [show, setShow] = createSignal(false);

  onMount(() => {
    if (!storage.getItem('language')) {
      setShow(true);
    }
  });

  const pick = async (code: string) => {
    await setLanguage(code);
    setShow(false);
  };

  return (
    <>
      <Show when={show()}>
        <div class="lps-overlay">
          <div class="lps-card">
            <div class="lps-logo">🐜</div>
            <h1 class="lps-title">King of Ants</h1>
            <p class="lps-subtitle">Choose your language · Wähle deine Sprache</p>
            <div class="lps-grid">
              {LANGUAGES.map((lang) => {
                const badge = STATUS_BADGE[lang.status];
                return (
                  <button type="button" class={`lps-btn lps-btn--${lang.status}`} onClick={() => pick(lang.code)}>
                    <span class="lps-flag">{lang.flag}</span>
                    <span class="lps-lang">{lang.native}</span>
                    <span class={badge.cls}>{badge.label}</span>
                  </button>
                );
              })}
            </div>
            <p class="lps-note">
              ✓ English is fully translated.<br />
              🚧 Other languages are work in progress and will show German as fallback.
            </p>
          </div>
        </div>
      </Show>
      <div style={{ visibility: show() ? 'hidden' : 'visible', height: show() ? '0' : 'auto', overflow: show() ? 'hidden' : 'visible' }}>
        {props.children}
      </div>
    </>
  );
}
